import reframe from 'nike-re-framejs';
import { BehaviorSubject, defer, EMPTY, from, merge as mergeNg, Observable, of, Subject } from 'rxjs';
import { filter, flatMap, map, merge, onErrorResumeNext, scan, startWith, tap } from 'rxjs/operators';
import { parseBetslip } from './betslipParser';
import { trackBetslipCmd, trackThirdPartyCreateDraft } from 'nike-ui/src/analytics/events/betslip';
import betslipClient from '@/_http/axiosBetslipClient';
import { getConfigProperty, getLocale } from '@/common/oldMobile';
import {
    enabledNewConflictsModal,
    openConflictingBetsDialog
} from 'nike-ui/src/components/betslip/conflicting-bets-dialog';
import { fromJS } from 'immutable';
import { isProfileNoBetslip } from 'nike-ui/src/profiles';
import { getStartingCommand } from 'nike-ui/src/components/betslip/commands/StartingCommand';

const log = (...args) => console.log('[command]', ...args);

const commandPath = cmd => {
    return `/${cmd}?lang=${getLocale()}`;
};

function callCmd(url) {
    function postCmdSuccess(cmd, observer) {
        return json => {
            observer.next(json);
            observer.complete();
        };
    }

    function postCmdFailed(err, cmd, observer) {
        if (err?.response?.status === 401) {
            betslipClient
                .post(url + commandPath(cmd[0]), cmd, observer)
                .then(postCmdSuccess(cmd, observer))
                .catch(error => {
                    log(cmd, error);
                    observer.error(null);
                });
        }
        if (err?.response?.status === 404 && isProfileNoBetslip()) {
            return Promise.resolve();
        }
        return Promise.reject(err);
    }

    return cmd => {
        return defer(() =>
            Observable.create(observer => {
                betslipClient
                    .post(url + commandPath(cmd[0]), cmd, observer)
                    .then(postCmdSuccess(cmd, observer), err => postCmdFailed(err, cmd, observer))
                    .catch(error => {
                        log(cmd, error);
                        observer.error(null);
                    });
            })
        );
    };
}

const betslipId$ = new BehaviorSubject(0);
const serverCommands$ = new BehaviorSubject(0);
const responseError$ = new Subject();
const responses$ = new Subject();
const response$ = new BehaviorSubject();

export const cmdStream$ = new Subject();
export const betslipSubmittedPush$ = new Subject();

const insertBetslipIndex = cmd => {
    if (cmd.length === 3) {
        return cmd;
    }
    const cmdLoc = cmd.slice();
    cmdLoc.splice(1, 0, betslipId$.getValue());
    return cmdLoc;
};

function dedupeStakeWaitCommands(cmd, wait) {
    while (
        cmd[0] === 'ChangeOverallMoneyStake' &&
        wait.length &&
        wait[wait.length - 1][0] === cmd[0] && // command
        wait[wait.length - 1][1] === cmd[1]
    ) {
        // betslip index
        wait.pop();
    }
    while (
        cmd[0] === 'ChangeSystemStake' &&
        wait.length &&
        wait[wait.length - 1][0] === cmd[0] && // command
        wait[wait.length - 1][1] === cmd[1] && // betslip index
        wait[wait.length - 1][2].stakeType === cmd[2].stakeType
    ) {
        // stake type
        wait.pop();
    }
}

const startingCommand = getStartingCommand();

cmdStream$
    .pipe(
        map(insertBetslipIndex),
        map(cmd => ['cmd', cmd]),
        merge(responseError$.pipe(map(isError => ['response', isError]))),
        scan(
            ([next, wait, inProgress], [type, cmd]) => {
                if (type === 'cmd') {
                    dedupeStakeWaitCommands(cmd, wait);
                    wait.push(cmd);
                } else if (type === 'response') {
                    if (!cmd) {
                        log('Got response, not error, running command', wait[0]);
                        return [wait[0], wait.slice(1), typeof wait[0] !== 'undefined'];
                    }
                    log('Got response, is error, clearing commands, type:', type);
                    return [undefined, [], false];
                }
                if (!inProgress) {
                    log('Not in progress, running cmd', wait[0]);
                    return [wait[0], wait.slice(1), typeof wait[0] !== 'undefined'];
                }
                log('In progress, waiting: ', wait);
                return [undefined, wait, true];
            },
            [undefined, [], false]
        ),
        tap(([next, wait, inProgress]) => {
            reframe.dispatch(['betslip/setCommandInProgress', inProgress, next, wait]);
        }),
        flatMap(([next]) => (typeof next !== 'undefined' ? from([next]) : from([]))),
        startWith(startingCommand)
    )
    .subscribe(serverCommands$);

serverCommands$
    .pipe(
        flatMap(cmd => {
            log('Running cmd', cmd);
            return of(cmd).pipe(
                flatMap(callCmd(getConfigProperty('betslipEndpoint'))),
                tap(
                    response => {
                        trackThirdPartyCreateDraft(cmd, response);
                        trackBetslipCmd(cmd, response?.betslip);
                    },

                    () => {
                        // nezobrazujeme betslip chyby v casine
                        if (!window.location.pathname.startsWith('/casino')) {
                            reframe.dispatch(['setServerError', 'ERROR_SERVER']);
                            return responseError$.next(true);
                        }
                    }
                ),
                onErrorResumeNext(EMPTY)
            );
        })
    )
    .subscribe(responses$);

responses$
    .pipe(
        tap(response => {
            if (response.lastCmdError && enabledNewConflictsModal(fromJS(response.lastCmdError))) {
                openConflictingBetsDialog(response.lastCmdError);
            }
        }),
        map(response => response && !!response.lastCmdError)
    )
    .subscribe(responseError$);

mergeNg(responses$, betslipSubmittedPush$)
    .pipe(
        map(parseBetslip),
        scan((oldBetslip, newBetslip) => {
            if (!oldBetslip) {
                return newBetslip;
            }
            if (newBetslip.getIn(['betslipIds', 0]) !== oldBetslip.getIn(['betslipIds', 0])) {
                return newBetslip;
            }
            if (newBetslip.get('version') < oldBetslip.get('version')) {
                return oldBetslip;
            }

            return newBetslip;
        }, null)
    )
    .subscribe(response$);

response$.pipe(filter(betslip => betslip)).subscribe(betslip => reframe.dispatch(['refreshBetslip', betslip]));

response$.pipe(filter(betslip => betslip)).subscribe(betslip => betslipId$.next(betslip.getIn(['betslipIndex'])));
